import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";

//========== Import components from MUI Library ============
//All Wrapper Elements
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Paper from "@mui/material/Paper";
import TableContainer from "@mui/material/TableContainer";
//All Loading related components
import Backdrop from "@mui/material/Backdrop";
//All CSS
import { makeStyles } from "tss-react/mui";

//All Element/ Controls
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
//All Icons
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";

// Others
import WorkspaceClass from "../../../../service/workspace";
import { setPrevState } from "../../../../util/common";
import { ButtonUI, KMKDialog } from "../../../Common";
import CircularProgressWithLabel from "../../../Common/CircularProgressWithLabel";
import { DATA_SOURCE } from "../../../../util/Data/DataModeling";

const columns = [
  { label: "Name", width: "50%", align: "left" },
  { label: "Status", width: "50%", align: "left" },
];

const columnsWithDisplayName = [
  { label: "Display name (System name)", width: "50%", align: "left" },
  { label: "Status", width: "50%", align: "left" },
];

const pipelineColumns = [
  { label: "Name", width: "33.33%", align: "left" },
  { label: "Status", width: "33.33%", align: "left" },
  { label: "Jobs", width: "33.33%", align: "left" },
];

const useStyles = makeStyles()((theme) => ({
  dialogPaper: {
    "& .MuiPaper-root.MuiDialog-paper": {
      maxWidth: "600px",
    },
  },
  formContainer: {
    display: "grid",
    width: "100%",
    textAlign: "left",
    minHeight: "100px",
  },
  container: {
    padding: "0px 15px",
    backgroundColor: "#FFFFFF",
    minHeight: "100px",
  },
  Toolbar: {
    width: "100%",
    fontSize: "1.1rem",
    backgroundColor: "white",
    color: "black",
  },
  DataTableContainer: {
    borderRadius: 4,
    backgroundColor: "#FFFFFF",
    padding: "0px",
    overflowX: "auto",
  },
  primaryTableHead: {
    "& .MuiTableCell-root": {
      backgroundColor: "#f0f0f0",
      color: "rgba(0, 0, 0, 0.87)",
      padding: "6px 16px",
      height: "37px",
    },
  },
  secondarytableHead: {
    "& .MuiTableCell-root": {
      color: "rgba(0, 0, 0, 0.87)",
      padding: "6px 16px",
      height: "37px",
    },
  },
  TableBody: {
    "& .MuiOutlinedInput-input": {
      padding: "4px 10px",
    },
    "& .MuiTableCell-root": {
      padding: "6px 16px",
      borderBottom: "none",
      wordBreak: "break-word",
    },
  },
  TableRow: {
    // Remove border from body's rows
    "&:last-child td": {
      border: 0,
    },
    "& .MuiSvgIcon-root": {
      fontSize: "24px",
    },
  },
}));

const DataTable = ({ searchTerm, historyList, mapLabel, columns }) => {
  const { classes } = useStyles();
  const mapList = historyList.filter((field) => {
    if (searchTerm === "") return field;

    const fieldName = field.displayName
      ? `${field.displayName} (${field.Name})`.toLowerCase()
      : field.Name.toLowerCase();

    return fieldName.toLowerCase().includes(searchTerm.toLowerCase());
  });

  return (
    <Paper sx={{ width: "100%", overflow: "hidden", marginBottom: "15px" }} className={classes.DataTableContainer}>
      <TableContainer
        sx={{
          maxHeight: "180px",
          maxWidth: "912px",
          flexGrow: 1,
        }}
      >
        <Table stickyHeader aria-label="sticky table" size="small">
          <TableHead>
            <TableRow className={classes.primaryTableHead}>
              <TableCell colSpan={columns.length} sx={{ fontWeight: 700 }}>
                {mapLabel}
              </TableCell>
            </TableRow>
            <TableRow className={classes.secondarytableHead}>
              {mapList.length ? (
                columns.map((header, index) => (
                  <TableCell
                    key={index}
                    sx={{ ...(header.width ? { width: header.width } : ""), fontWeight: 700, top: "37px" }}
                    align={header.align || "inherit"}
                  >
                    {header.label}
                  </TableCell>
                ))
              ) : (
                <></>
              )}
            </TableRow>
          </TableHead>
          <TableBody className={classes.TableBody}>
            {mapList.length ? (
              mapList.map(({ Name, displayName, Success, jobs }, i) => (
                <TableRow key={i} className={classes.TableRow}>
                  <TableCell align="left">{displayName ? `${displayName} (${Name})` : Name}</TableCell>
                  <TableCell align="left">{Success ? "Successful" : "Failed"}</TableCell>
                  {mapLabel === "Data Pipelines" && (
                    <TableCell align="left">
                      {`${jobs?.success ? `${jobs?.success} Success` : ""} ${
                        jobs?.error ? `${jobs?.error} Error` : ""
                      }`}
                    </TableCell>
                  )}
                </TableRow>
              ))
            ) : (
              <TableRow className={classes.TableRow}>
                <TableCell colSpan={2} component="td" scope="row" align={"center"}>
                  No data available
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

const ConfigHistory = (props) => {
  const { configState, submitData, importFrom, setConfigState, popupTitle, handleImportSummaryClose } = props;

  const { Workspace_id } = useParams();
  const [loading, setLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [data, setData] = useState({
    datasources: [],
    entities: [],
    connections: [],
    pipelines: [],
  });

  const dispatch = useDispatch();
  const { classes } = useStyles();

  useEffect(() => {
    const handleImport = async () => {
      setLoading(true);
      const workspace = new WorkspaceClass();
      const response = await workspace.importWorkspaceFrom(Workspace_id, importFrom, submitData);
      if (response.success) {
        setPrevState(setData, {
          ...(response?.datasources ? { datasources: response.datasources } : {}),
          ...(response?.entities ? { entities: response.entities } : {}),
          ...(response?.connections ? { connections: response.connections } : {}),
          ...(response?.pipelines ? { pipelines: response.pipelines } : {}),
        });
        setDataLoaded(true);
      } else {
        setDataLoaded(false);
        setPrevState(setConfigState, { openSummary: false });
      }
      setLoading(false);
    };

    handleImport();

    return () => {
      setPrevState(setConfigState, { openSummary: false });
    };
  }, [Workspace_id, configState, setConfigState, dispatch, importFrom, submitData]);

  return (
    <>
      <Backdrop
        sx={{ backgroundColor: "transparent", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading && !dataLoaded}
      >
        <CircularProgressWithLabel label="Generating summary" />
      </Backdrop>
      <KMKDialog className={classes.dialogPaper} open={dataLoaded} fullWidth>
        <AppBar sx={{ position: "relative" }}>
          <Toolbar className={classes.Toolbar}>{popupTitle} </Toolbar>
          <IconButton
            aria-label="close"
            onClick={() => handleImportSummaryClose()}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "black",
            }}
          >
            <CloseIcon />
          </IconButton>
        </AppBar>

        <DialogContent sx={{ m: 0, p: 0 }}>
          <Box sx={{ width: "100%" }} className={classes.container}>
            <Box
              sx={{
                display: "flex",
                width: "100%",
                alignItems: "center",
                justifyContent: "end",
                marginBottom: "15px",
              }}
            >
              <TextField
                variant="standard"
                placeholder="Search"
                margin="normal"
                value={searchTerm}
                id="outlined-size-small"
                InputProps={{
                  startAdornment: <SearchIcon color="primary" sx={{ marginRight: "5px" }} />,
                  ...(searchTerm && {
                    endAdornment: (
                      <IconButton onClick={() => setSearchTerm("")} size="small">
                        <ClearIcon color="primary" />
                      </IconButton>
                    ),
                  }),
                }}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ mr: "5px", width: { xs: "100%", sm: "37.5%" } }}
              />
            </Box>
            <Box sx={{ width: "100%" }} component="form" className={classes.formContainer}>
              <Grid container spacing={2}>
                <Grid size={12}>
                  {data?.datasources?.length ? (
                    <DataTable
                      searchTerm={searchTerm}
                      historyList={data.datasources}
                      mapLabel={DATA_SOURCE.plural}
                      columns={columnsWithDisplayName}
                    ></DataTable>
                  ) : (
                    <></>
                  )}
                  {data?.entities?.length ? (
                    <DataTable
                      searchTerm={searchTerm}
                      historyList={data.entities}
                      mapLabel="Entities"
                      columns={columnsWithDisplayName}
                    ></DataTable>
                  ) : (
                    <></>
                  )}
                  {data?.connections?.length ? (
                    <DataTable
                      searchTerm={searchTerm}
                      historyList={data.connections}
                      mapLabel="Connections"
                      columns={columns}
                    ></DataTable>
                  ) : (
                    <></>
                  )}
                  {data?.pipelines?.length ? (
                    <DataTable
                      searchTerm={searchTerm}
                      historyList={data.pipelines}
                      mapLabel="Data Pipelines"
                      columns={pipelineColumns}
                    ></DataTable>
                  ) : (
                    <></>
                  )}
                  {!data?.datasources?.length &&
                  !data?.entities?.length &&
                  !data?.connections?.length &&
                  !data?.pipelines?.length ? (
                    <Paper sx={{ width: "100%", overflow: "hidden" }} className={classes.DataTableContainer}>
                      <TableContainer sx={{ maxHeight: "180px", maxWidth: "912px", flexGrow: 1 }}>
                        <Table stickyHeader aria-label="sticky table" size="small">
                          <TableHead>
                            <TableRow className={classes.secondarytableHead}>
                              {columns.map((header, index) => (
                                <TableCell
                                  key={index}
                                  sx={{
                                    ...(header.width ? { width: header.width } : ""),
                                    fontWeight: 700,
                                  }}
                                  align={header.align || "inherit"}
                                >
                                  {header.label}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody className={classes.TableBody}>
                            <TableRow className={classes.TableRow}>
                              <TableCell colSpan={2} component="td" scope="row" align={"center"}>
                                No data available
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>
                  ) : (
                    <></>
                  )}
                </Grid>
              </Grid>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <ButtonUI onClick={handleImportSummaryClose} variant="contained" color="success" style={{ marginLeft: 15 }}>
            Ok
          </ButtonUI>
        </DialogActions>
      </KMKDialog>
    </>
  );
};

export default React.memo(ConfigHistory);
